// Ganchos de React sobre la base local.
import { liveQuery } from 'dexie';
import { useSyncExternalStore } from 'react';
import { db, type OperacionLocal, type Sesion } from './db';
import type { Paquete } from './tipos';
import { construirVista } from './vista';

export interface Estado {
  cargando: boolean;
  sesion: Sesion | null;
  paquete: Paquete | null;
  /** Paquete del servidor con las operaciones pendientes aplicadas: lo que se muestra. */
  vista: Paquete | null;
  cola: OperacionLocal[];
}

const INICIAL: Estado = { cargando: true, sesion: null, paquete: null, vista: null, cola: [] };

async function consultar(): Promise<Estado> {
  const [sesion, guardado, cola] = await Promise.all([
    db.sesion.get('actual'),
    db.paquete.get('actual'),
    db.cola.toArray(),
  ]);
  const paquete = guardado?.valor ?? null;
  return { cargando: false, sesion: sesion ?? null, paquete, vista: construirVista(paquete, cola), cola };
}

// Una sola consulta en vivo para toda la app: la guardia, los servicios, la pantalla y su marco leen el mismo estado
// en vez de armar cada uno la vista local por su cuenta (y una pantalla nueva lo tiene al instante, sin parpadeo).
let actual = INICIAL;
let suscripcion: { unsubscribe(): void } | null = null;
const escuchas = new Set<() => void>();

function suscribir(avisar: () => void): () => void {
  escuchas.add(avisar);
  suscripcion ??= liveQuery(consultar).subscribe({
    next: (v) => {
      actual = v;
      for (const e of escuchas) e();
    },
    error: (e) => console.error(e),
  });
  return () => {
    escuchas.delete(avisar);
    if (escuchas.size) return;
    suscripcion?.unsubscribe();
    suscripcion = null;
    actual = INICIAL;
  };
}

const leer = () => actual;

export function useEstado(): Estado {
  return useSyncExternalStore(suscribir, leer, leer);
}
